// App.js (Corrected Version)
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useAppState } from './context/AppStateContext';
import { useApi } from './hooks/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import StudentDashboard from './components/StudentDashboard';
import TeacherLandingRedirect from './components/teacher/TeacherLandingRedirect';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPasswordConfirm from './components/auth/ResetPasswordConfirm';
import ForcePasswordResetModal from './components/auth/ForcePasswordResetModal';
import Contact from './components/contact/Contact';
import Navbar from './components/common/Navbar';
import BottomNav from './components/common/BottomNav';
import LoadingSpinner from './components/common/LoadingSpinner';
import CourseDetail from './components/CourseDetail';
import ProfilePage from './components/ProfilePage';
import CourseList from './components/CourseList';
import AttendanceRecord from './components/AttendanceRecord';
import AdminDashboard from './pages/admin/AdminDashboard';
import TeacherCourseList from './pages/teacher/TeacherCourseList';
import TeacherCourseView from './pages/teacher/TeacherCourseView';
import ToastContainer from './components/common/ToastContainer';
import BulkGrading from './components/teacher/BulkGrading';
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentEvents from './pages/parent/ParentEvents';
import ChildFinances from './pages/parent/ChildFinances';
import SubmissionListPage from './pages/teacher/SubmissionListPage';
import GlobalAttendanceManager from './pages/teacher/GlobalAttendanceManager';
import GlobalSubmissionManager from './pages/teacher/GlobalSubmissionManager';
import TeacherClubs from './pages/teacher/TeacherClubs';
import SuccessHub from './components/student/SuccessHub';
import ParentReport from './pages/parent/ParentReport';
import StudentGlobalTasks from './pages/student/StudentGlobalTasks';
import ClassTeacherDashboard from './components/teacher/ClassTeacherDashboard';
import HodDashboard from './components/teacher/HodDashboard';
import LessonPlanManager from './pages/teacher/LessonPlanManager';
import LessonPlanEditor from './pages/teacher/LessonPlanEditor';
import LessonPlanTemplateManager from './pages/teacher/LessonPlanTemplateManager';
import ClassAttendanceManager from './pages/teacher/ClassAttendanceManager';
import StudentDetailsView from './pages/teacher/StudentDetailsView';
import ScholarRoster from './pages/teacher/ScholarRoster';
import ActiveMarkingPage from './pages/teacher/ActiveMarkingPage';
import GradeAssessmentPage from './pages/teacher/GradeAssessmentPage';
import StudentNoteView from './pages/student/StudentNoteView';
import CurriculumAreaManager from './pages/admin/CurriculumAreaManager';
import SchemeOfWorkPage from './pages/teacher/SchemeOfWorkPage';
import HOSDashboard from './pages/teacher/HOSDashboard';
import TeacherDisciplineManager from './pages/teacher/TeacherDisciplineManager';
import ParentDisciplinePage from './pages/parent/ParentDisciplinePage';
import TeacherManagement from './components/TeacherManagement';

// ─── Data-loading component (runs inside Router tree) ───────────────────────
function AppDataLoader() {
    const { isAuthenticated, loading: authLoading, user } = useAuth();
    const { dispatch, needsRefresh, setNeedsRefresh } = useAppState();
    const { get } = useApi();
    const queryClient = useQueryClient();

    // ── Teacher data: courses + attendance ───────────────────────────────────
    const teacherQueryKey = ['app-data', 'teacher', user?.id];
    const { data: teacherData, isFetching: teacherFetching } = useQuery({
        queryKey: teacherQueryKey,
        queryFn: async () => {
            const [attendanceData, coursesResponse] = await Promise.all([
                get('/teachers/api/class-attendance/'),
                get('/teachers/api/courses/'),
            ]);
            return { attendanceData, coursesResponse };
        },
        enabled: !!isAuthenticated && !authLoading && user?.role === 'teacher',
        staleTime: 5 * 60 * 1000,   // 5 min cache — no refetch on navigation
        gcTime: 30 * 60 * 1000,
    });

    // ── Student data: courses + attendance ───────────────────────────────────
    const studentQueryKey = ['app-data', 'student', user?.id];
    const { data: studentData, isFetching: studentFetching } = useQuery({
        queryKey: studentQueryKey,
        queryFn: async () => {
            const [attendanceData, enrolledCourses] = await Promise.all([
                get(`/students/${user.id}/attendance/`),
                get(`/students/${user.id}/courses/`),
            ]);
            return { attendanceData, enrolledCourses };
        },
        enabled: !!isAuthenticated && !authLoading && user?.role === 'student',
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

    // ── Global Performance Standards ─────────────────────────────────────────
    const bandsQueryKey = ['app-data', 'performance-bands'];
    const { data: bandsData } = useQuery({
        queryKey: bandsQueryKey,
        queryFn: () => get('/api/performance-bands/'),
        enabled: !!isAuthenticated && !authLoading,
        staleTime: 60 * 60 * 1000, // 1 hour cache
    });

    // ── needsRefresh compatibility: manual refresh → TQ invalidation ─────────
    // When anything in the app calls refresh(), we invalidate the TQ cache
    // so the next render triggers a background refetch.
    useEffect(() => {
        if (needsRefresh) {
            queryClient.invalidateQueries({ queryKey: ['app-data'] });
            setNeedsRefresh(false);
        }
    }, [needsRefresh, queryClient, setNeedsRefresh]);

    // ── Sync TQ results → AppStateContext (keep downstream consumers unchanged)
    useEffect(() => {
        if (!teacherData) return;
        const { attendanceData, coursesResponse } = teacherData;
        const courses = Array.isArray(coursesResponse?.courses) ? coursesResponse.courses : [];
        const managedClubs = Array.isArray(coursesResponse?.managed_clubs) ? coursesResponse.managed_clubs : [];
        const pendingActions = Array.isArray(coursesResponse?.pending_actions) ? coursesResponse.pending_actions : [];

        dispatch({ type: 'SET_TEACHER_ATTENDANCE', payload: Array.isArray(attendanceData) ? attendanceData : [] });
        dispatch({ type: 'SET_COURSES', payload: courses });
        dispatch({ type: 'SET_MANAGED_CLUBS', payload: managedClubs });
        dispatch({ type: 'SET_CLASS_TEACHER', payload: !!coursesResponse?.is_class_teacher });
        dispatch({ type: 'SET_HOD', payload: !!coursesResponse?.is_hod });
        dispatch({ type: 'SET_HOS', payload: !!coursesResponse?.is_head_of_studies });
        dispatch({ type: 'SET_PRINCIPAL', payload: !!coursesResponse?.is_principal });
        dispatch({ type: 'SET_UNIQUE_STUDENT_COUNT', payload: Number(coursesResponse?.unique_student_count || 0) });
        dispatch({ type: 'SET_PENDING_ACTIONS', payload: pendingActions });
    }, [teacherData, dispatch]);

    useEffect(() => {
        if (!studentData) return;
        const { attendanceData, enrolledCourses } = studentData;
        dispatch({ type: 'SET_STUDENT_ATTENDANCE', payload: Array.isArray(attendanceData) ? attendanceData : [] });
        dispatch({ type: 'SET_COURSES', payload: Array.isArray(enrolledCourses) ? enrolledCourses : [] });
    }, [studentData, dispatch]);

    useEffect(() => {
        if (bandsData) {
            dispatch({ type: 'SET_PERFORMANCE_BANDS', payload: bandsData });
        }
    }, [bandsData, dispatch]);

    // ── Loading state → AppStateContext ──────────────────────────────────────
    useEffect(() => {
        const loading = teacherFetching || studentFetching;
        dispatch({ type: 'SET_LOADING', payload: loading });
    }, [teacherFetching, studentFetching, dispatch]);

    return null; // purely side-effectful
}


function App() {
    const { isAuthenticated, loading: authLoading, user } = useAuth();

    if (authLoading) {
        return <LoadingSpinner fullScreen />;
    }

    return (
        <Router>
            <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
                {/* AppDataLoader fires useQuery hooks for teacher/student — cached for 5 min */}
                <AppDataLoader />
                <Navbar />
                <Routes>
                    <Route
                        path="/login"
                        element={!isAuthenticated ? <Login /> : <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />}
                    />
                    <Route
                        path="/forgot-password"
                        element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" replace />}
                    />
                    <Route
                        path="/password-reset/confirm"
                        element={!isAuthenticated ? <ResetPasswordConfirm /> : <Navigate to="/dashboard" replace />}
                    />
                    <Route
                        path="/signup"
                        element={!isAuthenticated ? <Signup /> : <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />}
                    />
                    <Route path="/contact" element={<Contact />} />
                    <Route
                        path="/dashboard"
                        element={
                            isAuthenticated ? (
                                user?.role === 'admin' ? (
                                    <Navigate to="/admin" replace />
                                ) : user?.role === 'teacher' ? (
                                    // If HOD and has no teaching courses, land on HOD dashboard
                                    // Otherwise general teacher dashboard
                                    <TeacherLandingRedirect />
                                ) : user?.role === 'parent' ? (
                                    <Navigate to="/parent" replace />
                                ) : (
                                    <StudentDashboard />
                                )
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/courses/api/:id/"
                        element={
                            isAuthenticated ? (
                                <CourseDetail />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/courses/:id/notes/:noteId"
                        element={
                            isAuthenticated ? (
                                <StudentNoteView />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            isAuthenticated ? (
                                <ProfilePage />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            isAuthenticated ? (
                                <ProfilePage />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/courses"
                        element={
                            isAuthenticated ? (
                                user?.role === 'student' ? (
                                    <CourseList />
                                ) : (
                                    <Navigate to="/dashboard" replace />
                                )
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/success-hub"
                        element={
                            isAuthenticated && user?.role === 'student' ? (
                                <SuccessHub />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/tasks"
                        element={
                            isAuthenticated && user?.role === 'student' ? (
                                <StudentGlobalTasks />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/student/assignments/:assignmentId/feedback/:submissionId"
                        element={
                            isAuthenticated && user?.role === 'student' ? (
                                <ActiveMarkingPage />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />

                    <Route
                        path="/attendance"
                        element={
                            isAuthenticated ? (
                                user?.role === 'student' ? (
                                    <AttendanceRecord />
                                ) : (
                                    <Navigate to="/dashboard" replace />
                                )
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/courses/:courseId/grade/:assignmentId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <GradeAssessmentPage />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/admin/*"
                        element={
                            isAuthenticated && user?.is_superuser ? (
                                <AdminDashboard />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/courses"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <TeacherCourseList />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/courses/:id"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <TeacherCourseView />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/curriculum-manager/:areaId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <CurriculumAreaManager />
                            ) : (
                                <Navigate to="/login" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/attendance"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <GlobalAttendanceManager />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/submissions"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <GlobalSubmissionManager />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/clubs"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <TeacherClubs />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/clubs/:clubId/grade/:assignmentId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <GradeAssessmentPage />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/bulk-grading/:assignmentId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <BulkGrading />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/assignments/:assignmentId/submissions"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <SubmissionListPage />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/assignments/:assignmentId/mark/:submissionId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <ActiveMarkingPage />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/class-dashboard"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <ClassTeacherDashboard />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/hod-dashboard"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <HodDashboard />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/hos-dashboard"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <HOSDashboard />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/class-roster"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <ScholarRoster />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/scheme-of-work/:courseId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <SchemeOfWorkPage />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/class-attendance"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <ClassAttendanceManager />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/student-detail/:studentId"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <StudentDetailsView />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/lesson-plans"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <LessonPlanManager />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/lesson-plan/editor/:id"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <LessonPlanEditor />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/lesson-plans/templates"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <LessonPlanTemplateManager />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/discipline"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <TeacherDisciplineManager />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/teacher/hod-discipline"
                        element={<Navigate to="/teacher/discipline" replace />}
                    />

                    <Route
                        path="/teacher/classes-grading"
                        element={
                            isAuthenticated && user?.role === 'teacher' ? (
                                <TeacherManagement isStandalone={true} />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />

                    <Route
                        path="/parent"
                        element={
                            isAuthenticated && user?.role === 'parent' ? (
                                <ParentDashboard />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/parent/discipline"
                        element={
                            isAuthenticated && user?.role === 'parent' ? (
                                <ParentDisciplinePage />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/parent/events"
                        element={
                            isAuthenticated && user?.role === 'parent' ? (
                                <ParentEvents />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/parent/child/:childId/progress"
                        element={
                            isAuthenticated && user?.role === 'parent' ? (
                                <ParentReport />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/parent/child/:childId/finances"
                        element={
                            isAuthenticated && user?.role === 'parent' ? (
                                <ChildFinances />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )
                        }
                    />
                    <Route
                        path="/"
                        element={<Navigate to={isAuthenticated ? (user?.role === 'admin' ? '/admin/dashboard' : '/dashboard') : '/login'} replace />}
                    />
                </Routes>
                <BottomNav />
                <ToastContainer />
                <ForcePasswordResetModal />
            </div>
        </Router>
    );
}

export default App;